import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import {
  Alert,
  Card,
  Field,
  Page,
  PageHeader,
  StatCard,
  cn,
  linkTextClasses,
} from '@evogard/ui';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@evogard/ui/primitives';
import type { ManagementClaim } from '@evogard/contract';
import {
  CLAIM_CLOSURES_DEFAULT_DAYS,
  CLAIM_CLOSURE_KINDS,
  count,
  isClaimConclusion,
  type ManagementStats,
} from '@evogard/domain';
import { useSessionContext } from '../../../lib/session.js';
import { useDebouncedTerm } from '../../../lib/debounce.js';
import { ClaimDeskModal } from '../claim/ClaimDeskModal.js';
import { showUndoToast } from '../claim/UndoMove.js';
import { useClaim } from '../claim/useClaims.js';
import { useClosureList } from '../closures/useClosures.js';
import { PeripheralDeskModal } from '../peripherals/PeripheralDeskModal.js';
import { ManagementPanel } from './ManagementPanel.js';
import { EndPanel, type OpenEnd } from './EndPanel.js';
import { ManagementLines } from './ManagementLines.js';
import { PointPanel } from './PointPanel.js';
import { LINE_LABEL, LINE_TONE, numberOf } from './managementLabels.js';
import {
  useManagementMap,
  useManagementPermissions,
  type ManagementOnly,
  type ManagementPoint,
} from './useManagement.js';

/** De quem é o fluxo em linhas: dos três departamentos, ou de Periféricos (28/10/2026). */
export type LinesScope = 'sinistro' | 'perifericos';

/**
 * Sinistros › Gestão (23/10/2026): o sinistro inteiro num mapa só.
 *
 * Cada departamento é um trecho da avenida — o fluxo ótimo dele —, as
 * situações são sub-paradas, os desvios correm em marginais e cada acionamento
 * em processo é uma bolinha onde está. Periféricos tem a sua aba, no mesmo
 * desenho. Só leitura: o clique abre a mesa do departamento, que cobra o
 * recurso de cada ato.
 *
 * As decisões do dono estão no domínio (`claims-management.ts`): o vermelho é
 * o SLA da fase expirado; o travado fica na estação, marcado; quem vê a
 * Gestão vê a Supervisão do Reparo. O desenho é o do artefato da Mesa dos
 * Departamentos.
 *
 * **Periféricos saiu daqui em 28/10/2026** (pedido do dono): tem a sua
 * Gestão, com painel próprio (`PeripheralManagementScreen`), e o fluxo em
 * linhas dele mora lá. Este fluxo é o dos três departamentos.
 *
 * **Duas sub-abas (26/10/2026)**: o fluxo em linhas responde "onde está cada
 * acionamento agora"; o Painel, com os gráficos, responde "a
 * operação está andando". Perguntas diferentes, com recortes diferentes — o
 * mapa é o momento, o painel é um período —, e por isso não dividem a tela: o
 * mapa é alto, e o gráfico embaixo dele ficaria fora de vista, em cima
 * empurraria o mapa. **O painel é a aba principal** (decisão do dono em
 * 26/10/2026): a Gestão abre nele, e o fluxo em linhas fica no endereço
 * (`?view=linhas`). O conteúdo da aba fechada não é montado: cada uma só
 * consulta quando alguém a abre.
 */
export function ManagementScreen() {
  const { data: context } = useSessionContext();
  const view = useSearch({ from: '/authenticated/claims/management' }).view;
  const navigate = useNavigate();
  const tenant = context?.currentTenant.nome;
  return (
    <Page measure="cheia">
      <PageHeader title="Gestão do sinistro">
        {view !== 'linhas'
          ? `Se a operação de ${tenant} está andando: por onde os sinistros passam e como terminam. Os gráficos olham um período; o fluxo em linhas mostra o momento.`
          : `Onde está cada sinistro em processo de ${tenant}. Cada raia é um departamento, a estrada é o caminho normal dele e cada marcador é uma etapa, com os sinistros que estão nela; os desvios descem dos losangos. O vermelho é o SLA da fase expirado.`}
      </PageHeader>
      <Tabs
        value={view ?? 'painel'}
        onValueChange={(value) =>
          void navigate({
            to: '/claims/management',
            search: { view: value === 'linhas' ? 'linhas' : undefined },
            replace: true,
          })
        }
      >
        <TabsList aria-label="Visões da gestão">
          <TabsTrigger value="painel">Painel</TabsTrigger>
          <TabsTrigger value="linhas">Fluxo em linhas</TabsTrigger>
        </TabsList>
        <TabsContent value="painel" className="pt-5">
          <ManagementPanel />
        </TabsContent>
        <TabsContent value="linhas" className="pt-5">
          <LinesView scope="sinistro" />
        </TabsContent>
      </Tabs>
    </Page>
  );
}

/**
 * O fluxo em linhas: o mapa, o resumo e a busca, e o que se abre a partir
 * deles. O mapa vem inteiro da API, e cada Gestão desenha a sua parte.
 */
export function LinesView({ scope }: { scope: LinesScope }) {
  const perms = useManagementPermissions();
  const [search, setSearch] = useState('');
  const term = useDebouncedTerm(search).trim();
  const [slaOnly, setSlaOnly] = useState(false);
  const query = useManagementMap(term);
  const map = query.data;

  const ours = (line: string) =>
    scope === 'perifericos' ? line === 'perifericos' : line !== 'perifericos';
  const lines = useMemo(() => (map?.lines ?? []).filter((l) => ours(l.line)), [map]);
  const sum = (key: 'total' | 'fresh' | 'slaExpired' | 'thirdParty') =>
    map ? lines.reduce((n, l) => n + l[key], 0) : undefined;
  const found = (map?.found ?? []).filter((c) => ours(c.line));

  const [opened, setOpened] = useState<{
    point: ManagementPoint;
    title: string;
    only?: ManagementOnly;
  } | null>(null);
  const [end, setEnd] = useState<OpenEnd | null>(null);
  /* O fim abre a lista de Encerrados: sem a permissão de lá, ele mostra o número e não abre. */
  const openEnd = useMemo(
    () =>
      perms.canSeeClosures && map
        ? (line: OpenEnd['line'], kind: OpenEnd['kind']) =>
            setEnd({ line, kind, since: map.closuresSince })
        : null,
    [perms.canSeeClosures, map],
  );
  const [claimId, setClaimId] = useState<number | null>(null);
  const [peripheralId, setPeripheralId] = useState<number | null>(null);
  const claim = useClaim(claimId);

  const canOpen = (c: ManagementClaim) =>
    c.source === 'periferico' ? perms.canOpenPeripheral : perms.canOpenClaim;
  const openClaim = (c: ManagementClaim) => {
    if (!canOpen(c)) return;
    if (c.source === 'periferico') setPeripheralId(c.id);
    else setClaimId(c.id);
  };

  /* Os números do ponto aberto, do mapa que já chegou. */
  const openedStats = useMemo((): ManagementStats | null => {
    if (!opened || !map) return null;
    const stage = map.lines
      .find((l) => l.line === opened.point.line)
      ?.phases.find((p) => p.phase === opened.point.phase);
    if (!stage) return null;
    if (opened.point.step === 'estacao') return stage.station;
    if (opened.point.step)
      return stage.steps.find((s) => s.situation === opened.point.step) ?? null;
    return stage;
  }, [opened, map]);

  return (
    <>
      {query.isError && !map && <Alert tone="erro">{query.error.message}</Alert>}

      <div className="grid items-start gap-4 lg:grid-cols-[17rem_minmax(0,1fr)]">
        <aside className="flex flex-col gap-4" aria-label="Resumo e busca">
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-1">
            <StatCard
              icon="escudo"
              label="Em processo"
              value={sum('total')}
              density="densa"
            />
            <StatCard
              icon="relogio"
              label="SLA da fase expirado"
              value={sum('slaExpired')}
              density="densa"
              tone={(sum('slaExpired') ?? 0) > 0 ? 'erro' : 'neutro'}
              support={
                slaOnly ? 'Destacando só onde há SLA expirado' : 'Clique para destacar'
              }
              onClick={() => setSlaOnly((v) => !v)}
              pressed={slaOnly}
            />
            <StatCard
              icon="calendario"
              label="Abertos nas últimas 24 h"
              value={sum('fresh')}
              density="densa"
            />
            {scope === 'sinistro' && (
              <StatCard
                icon="terceiro"
                label="De terceiros"
                value={sum('thirdParty')}
                density="densa"
              />
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <Field
              label="Achar um acionamento"
              type="search"
              icon="busca"
              placeholder="Número, nome ou placa"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <p className="text-xs text-texto-suave" aria-live="polite">
              {!term
                ? 'O que não casa fica apagado, no lugar onde está.'
                : found.length === 0
                  ? 'Nenhum acionamento com esse termo.'
                  : `${found.length === 1 ? '1 encontrado' : `${found.length} primeiros encontrados`}: ${found
                      .map((c) => `${numberOf(c)} em ${LINE_LABEL[c.line]}`)
                      .join(', ')}`}
            </p>
          </div>

          <Legend scope={scope} />
        </aside>

        <Card bleed>
          <div className="px-4 py-4">
            {!map ? (
              <p className="text-sm text-texto-suave">Carregando o mapa…</p>
            ) : (
              <ManagementLines
                lines={lines}
                dimming={{ searching: term !== '', slaOnly }}
                canOpen={canOpen}
                onOpenClaim={openClaim}
                onOpenPoint={(point, title, only) => setOpened({ point, title, only })}
                onOpenEnd={openEnd}
              />
            )}
            {perms.canSeeClosures && <ClosuresStrip scope={scope} />}
          </div>
        </Card>
      </div>

      <PointPanel
        key={opened ? JSON.stringify([opened.point, opened.only]) : 'fechado'}
        point={opened?.point ?? null}
        title={opened?.title ?? ''}
        initialOnly={opened?.only}
        stats={openedStats}
        canOpen={canOpen}
        onOpenClaim={openClaim}
        onClose={() => setOpened(null)}
      />
      {end && (
        <EndPanel
          key={`${end.line}-${end.kind}`}
          end={end}
          canOpen={(c) =>
            c.source === 'periferico' ? perms.canOpenPeripheral : perms.canOpenClaim
          }
          onOpen={(c) => {
            setEnd(null);
            if (c.source === 'periferico') setPeripheralId(c.id);
            else setClaimId(c.id);
          }}
          onClose={() => setEnd(null)}
        />
      )}
      <PeripheralDeskModal
        key={peripheralId ?? 'fechado'}
        peripheralId={peripheralId}
        onClose={() => setPeripheralId(null)}
      />
      {/* A mesa abre quando a ficha chega: ela é a linha que a mesa pede. */}
      <ClaimDeskModal
        key={claimId ?? 'fechado'}
        claim={claimId !== null && claim.data ? claim.data : null}
        onClose={() => setClaimId(null)}
        onOpenClaim={(other) => setClaimId(other.id)}
        onMoved={(move) => {
          /* Como nos quadros: moveu, a mesa fecha e o Desfazer vai para o canto. */
          showUndoToast(move);
          setClaimId(null);
        }}
        lastMove={null}
        onLastMoveDone={() => undefined}
      />
    </>
  );
}

/**
 * Como ler: as formas são as dos fluxogramas da casa, e cada sinal do
 * acionamento tem a sua forma, e não só a sua cor.
 */
function Legend({ scope }: { scope: LinesScope }) {
  /* As amostras na cor da linha que está na tela. */
  const tone = LINE_TONE[scope === 'perifericos' ? 'perifericos' : 'analise'];
  const mark = 'inline-block shrink-0';
  const dot = cn(mark, 'size-4 rounded-pilula border-2 bg-superficie');
  return (
    <section
      aria-label="Como ler"
      className="flex flex-col gap-2 text-xs text-texto-suave"
    >
      <h2 className="rotulo">Como ler</h2>
      <p className="flex items-center gap-2">
        <span aria-hidden className={cn(mark, 'h-1 w-5 rounded-pilula', tone.fill)} />
        estrada: o caminho normal do departamento
      </p>
      <p className="flex items-center gap-2">
        <svg aria-hidden width="13" height="17" viewBox="0 0 26 34" className={mark}>
          <path
            d="M13 33C13 33 2 20.5 2 12.5A11 11 0 0 1 24 12.5C24 20.5 13 33 13 33Z"
            className={tone.head}
          />
          <circle cx="13" cy="12.5" r="4.6" className="fill-superficie" />
        </svg>
        etapa: quantos estão nela; abaixo, as situações e os parados
      </p>
      <p className="flex items-center gap-2">
        <span
          aria-hidden
          className={cn(
            mark,
            'mx-0.5 size-3 rotate-45 rounded-[2px] border-[1.5px] border-fase-amarelo bg-fase-amarelo-suave',
          )}
        />
        a etapa se divide: segue a estrada ou desvia para baixo
      </p>
      <p className="flex items-center gap-2">
        <span
          aria-hidden
          className={cn(mark, 'w-5 border-t-2 border-dashed border-texto-tenue')}
        />
        tracejado: o desvio volta para a estrada
      </p>
      <p className="flex items-center gap-2">
        <span
          aria-hidden
          className={cn(
            mark,
            'size-4 rounded-pilula border-2 border-fase-verde bg-fase-verde-suave',
          )}
        />
        início
      </p>
      <p className="flex items-center gap-2">
        <span
          aria-hidden
          className={cn(
            mark,
            'size-4 rounded-pilula border-[3px] border-fase-vermelho bg-fase-vermelho-suave',
          )}
        />
        fim: concluído ou negativa
      </p>
      <p className="flex items-center gap-2">
        <span
          aria-hidden
          className={cn(
            mark,
            'size-4 rounded-pilula border border-texto-suave outline-[1.5px] outline-offset-1 outline-texto-suave outline-solid',
          )}
        />
        conector: a seta continua na mesma letra
      </p>
      <p className="flex items-center gap-2">
        <span aria-hidden className={cn(dot, tone.border)} />
        um acionamento, com as iniciais
      </p>
      {scope === 'sinistro' && (
        <p className="flex items-center gap-2">
          <span aria-hidden className={cn(dot, 'border-dashed', tone.border)} />
          tracejado: de terceiro
        </p>
      )}
      <p className="flex items-center gap-2">
        <span aria-hidden className={cn(dot, 'border-erro ring-2 ring-erro-suave')} />
        vermelho: SLA da fase expirado
      </p>
      <p className="flex items-center gap-2">
        <span aria-hidden className={cn(dot, 'relative', tone.border)}>
          <span className="absolute -top-1 -right-1 size-2 rounded-pilula bg-alerta" />
        </span>
        ponto amarelo: parado na etapa
      </p>
      <p className="flex items-center gap-2">
        <span aria-hidden className={cn(dot, 'bg-primaria-suave', tone.border)} />
        fundo laranja: aberto nas últimas 24 h
      </p>
      <p>
        Etapa com mais de quatro vira pilha, e a parte vermelha é a do SLA expirado.
        Clique numa etapa ou numa situação para ver a lista, e num acionamento para abrir
        a mesa.
      </p>
    </section>
  );
}

/**
 * Os que saíram do mapa: concluídos, desistências e negativas, no período
 * padrão de Encerrados. A lista de lá filtra por UM departamento; o do
 * sinistro é o todo menos Periféricos, para as duas Gestões não contarem o
 * mesmo encerrado.
 */
function ClosuresStrip({ scope }: { scope: LinesScope }) {
  const all = useClosureList({}, 1, 1);
  const peripherals = useClosureList({ origin: 'perifericos' }, 1, 1);
  const counts =
    scope === 'perifericos'
      ? peripherals.data?.counts
      : all.data && peripherals.data
        ? Object.fromEntries(
            CLAIM_CLOSURE_KINDS.map((k) => [
              k,
              (all.data.counts[k] ?? 0) - (peripherals.data.counts[k] ?? 0),
            ]),
          )
        : undefined;
  const total = (pick: (k: (typeof CLAIM_CLOSURE_KINDS)[number]) => boolean) =>
    counts
      ? CLAIM_CLOSURE_KINDS.filter(pick).reduce((n, k) => n + (counts[k] ?? 0), 0)
      : null;
  const concluded = total(isClaimConclusion);
  const withdrawn = total((k) => k === 'desistencia');
  const denied = total((k) => k === 'negado');
  return (
    <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-borda border border-dashed border-borda-forte px-4 py-3 text-sm text-texto-suave">
      <span className="font-semibold text-texto">Encerrados</span>
      <span className="flex-1">
        {counts
          ? `${count(concluded!, 'concluído', 'concluídos')}, ${count(withdrawn!, 'desistência', 'desistências')} e ${count(denied!, 'negativa', 'negativas')} nos últimos ${CLAIM_CLOSURES_DEFAULT_DAYS} dias${scope === 'perifericos' ? ', em Periféricos' : ', na Análise, no Reparo e na Indenização'}.`
          : 'Carregando…'}
      </span>
      <Link to="/claims/closed" className={linkTextClasses()}>
        Ver encerrados
      </Link>
    </div>
  );
}
